import { useCallback, useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SectionCard, Field, Skeleton } from '../pipeline/ui';

const EMPTY_PROMO = { id: null, code: '', credits: '', max_redemptions: '', expires_at: '', active: true };

// Full CRUD editor for promo_codes (super-admin), mirroring the bundle-admin pattern. No hardcoded
// codes — rows come from list_promos. Create / Edit / Deactivate-Restore via the 032 RPCs.
export function PromoEditor({ promos = [], onSave, onToggleActive }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_PROMO);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const edit = (p) => {
    setForm({
      id: p.id, code: p.code ?? '', credits: p.credits ?? '',
      max_redemptions: p.max_redemptions ?? '',
      expires_at: p.expires_at ? String(p.expires_at).slice(0, 10) : '',
      active: p.active !== false,
    });
    setEditing(true);
  };
  const reset = () => { setForm(EMPTY_PROMO); setEditing(false); };

  const submit = async () => {
    if (!String(form.code).trim()) { toast.error(t('promoAdmin.codeRequired')); return; }
    if (!(Number(form.credits) > 0)) { toast.error(t('promoAdmin.creditsRequired')); return; }
    setSaving(true);
    try { await onSave(form); toast.success(t('promoAdmin.saved')); reset(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <SectionCard title={t('promoAdmin.title')} testId="promos-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="promos-table">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-4"><T k="promoAdmin.colCode" /></th>
              <th className="text-start py-2 pe-4"><T k="promoAdmin.colCredits" /></th>
              <th className="text-start py-2 pe-4"><T k="promoAdmin.colMax" /></th>
              <th className="text-start py-2 pe-4"><T k="promoAdmin.colRedeemed" /></th>
              <th className="text-start py-2 pe-4"><T k="promoAdmin.colExpires" /></th>
              <th className="text-start py-2 pe-4"><T k="promoAdmin.colActive" /></th>
              <th className="text-start py-2"></th>
            </tr>
          </thead>
          <tbody className="font-data">
            {promos.length === 0 ? (
              <tr><td colSpan="7" className="py-3 text-muted-foreground" data-testid="promos-empty">—</td></tr>
            ) : promos.map((p) => (
              <tr key={p.id} className={`border-b border-border ${p.active === false ? 'opacity-50' : ''}`} data-testid={`promo-row-${p.id}`}>
                <td className="py-2 pe-4 text-foreground uppercase">{p.code}</td>
                <td className="py-2 pe-4">{Number(p.credits).toLocaleString()}</td>
                <td className="py-2 pe-4">{p.max_redemptions == null ? t('promoAdmin.unlimited') : p.max_redemptions}</td>
                <td className="py-2 pe-4 text-muted-foreground">{p.redeemed_count ?? 0}</td>
                <td className="py-2 pe-4 text-muted-foreground">{p.expires_at ? String(p.expires_at).slice(0, 10) : t('promoAdmin.never')}</td>
                <td className="py-2 pe-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${p.active === false ? 'bg-muted text-muted-foreground border-border' : 'bg-success-soft text-success border-success-border'}`} data-testid={`promo-active-${p.id}`}>
                    {p.active === false ? t('promoAdmin.inactive') : t('promoAdmin.active')}
                  </span>
                </td>
                <td className="py-2 text-end whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => edit(p)} data-testid={`promo-edit-${p.id}`}><T k="promoAdmin.edit" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onToggleActive(p.id, p.active === false)} data-testid={`promo-toggle-active-${p.id}`}>
                    {p.active === false ? t('promoAdmin.activate') : t('promoAdmin.deactivate')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-foreground" data-testid="promo-form-title">{editing ? t('promoAdmin.editPromo') : t('promoAdmin.addPromo')}</h4>
          {editing && <Button variant="ghost" size="sm" onClick={reset} data-testid="promo-cancel"><T k="promoAdmin.cancel" /></Button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={<T k="promoAdmin.colCode" />}>
            <Input value={form.code} disabled={editing} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="WELCOME50" data-testid="promo-code" />
          </Field>
          <Field label={<T k="promoAdmin.colCredits" />}>
            <Input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="promo-credits" />
          </Field>
          <Field label={<T k="promoAdmin.colMax" />}>
            <Input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} data-testid="promo-max" />
            <p className="text-xs text-muted-foreground mt-1"><T k="promoAdmin.maxHint" /></p>
          </Field>
          <Field label={<T k="promoAdmin.colExpires" />}>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-expires" />
            <p className="text-xs text-muted-foreground mt-1"><T k="promoAdmin.expiresHint" /></p>
          </Field>
        </div>
        <div className="mt-4">
          <Button onClick={submit} disabled={saving} data-testid="promo-save">
            {saving ? t('promoAdmin.saving') : (editing ? t('promoAdmin.updatePromo') : t('promoAdmin.addPromo'))}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// Admin-editable free-rounds default (reuses wallet_config). No hardcoded N.
export function FreeRoundsConfig({ value = 0, onSave }) {
  const { t } = useTranslation();
  const [n, setN] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setN(value); }, [value]);
  const save = async () => {
    setSaving(true);
    try { await onSave(Math.max(0, Math.floor(Number(n) || 0))); toast.success(t('promoAdmin.freeRoundsSaved')); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <SectionCard title={t('promoAdmin.freeRoundsTitle')} testId="free-rounds-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="promoAdmin.freeRoundsHint" /></p>
      <div className="flex items-end gap-3">
        <Field label={<T k="promoAdmin.freeRoundsTitle" />}>
          <Input type="number" value={n} onChange={(e) => setN(e.target.value)} data-testid="free-rounds-input" className="w-32" />
        </Field>
        <Button onClick={save} disabled={saving} data-testid="free-rounds-save"><T k="promoAdmin.freeRoundsSave" /></Button>
      </div>
    </SectionCard>
  );
}

// Container: fetches promos + wallet_config (for free-rounds default) and wires the passthroughs.
export default function PromoAdmin() {
  const [promos, setPromos] = useState(null);
  const [freeRounds, setFreeRounds] = useState(0);

  const load = useCallback(() => {
    api.tenantAdmin('list_promos').then((r) => setPromos(r.promos || [])).catch((e) => toast.error(e.message));
    api.tenantAdmin('get_wallet_config').then((r) => setFreeRounds(Number(r.config?.free_rounds_default ?? 0))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async (form) => {
    await api.tenantAdmin('upsert_promo', {
      id: form.id || undefined,
      code: form.code,
      credits: Number(form.credits),
      max_redemptions: form.max_redemptions === '' ? null : Number(form.max_redemptions),
      expires_at: form.expires_at || null,
      active: form.active,
    });
    load();
  };
  const onToggleActive = async (id, active) => {
    try { await api.tenantAdmin('set_promo_active', { id, active }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const onSaveFreeRounds = async (free_rounds_default) => {
    await api.tenantAdmin('set_free_rounds', { free_rounds_default });
    load();
  };

  if (promos === null) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="flex flex-col gap-6">
      <PromoEditor promos={promos} onSave={onSave} onToggleActive={onToggleActive} />
      <FreeRoundsConfig value={freeRounds} onSave={onSaveFreeRounds} />
    </div>
  );
}
